import { React, useEffect, useState } from 'react';
import { Flex, Box, Text, Divider, Radio, RadioGroup, Stack } from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { Controller, useFormContext } from 'react-hook-form';
import { debounce } from 'lodash';
import { metadataOptions, selectStyles } from '../../helpers/chakraSelect';
import TooltipIcon from './TooltipIcon';
import TextAreaBox from './TextAreaBox';
import InputBox from './InputBox';
import SoftSkillsCheckbox from './SoftSkillsCheckbox';
import StrongestTechStack from './StrongestTechStack';
import FlagsCheckboxes from './FlagsCheckboxes';
import GreenTitleBox from './GreenTitleBox';
import SimpleCheckbox from './SimpleCheckbox';
import StyledAccordionItem from './StyledAccordionItem';
import { sendAmplitudeData } from '../../utils/amplitudeUtils';
import { useDispatch, useTrackedState } from '../../config/store';

function InterviewAccordion({ interview, metadata, experienceMetadata }) {
  const methods = useFormContext();
  const dispatch = useDispatch();
  const state = useTrackedState();
  const [checklist, setChecklist] = useState([]);

  const [shadowSession, setShadowSession] = useState(false);

  const handlerSalaryInputOnChange = event => {
    const { value } = event.target;
    const lastChar = value[value.length - 1];
    if (lastChar === ',' || lastChar === '.') {
      const newValue = value.slice(0, -1);
      return newValue;
    }
    return value;
  };

  const optionsWithDisabled = metadataOptions(metadata, 'Interview_Blocked_Reason').map(option => ({
    ...option,
    isDisabled: option.value === 'Cancelled' || option.value === 'Rescheduled',
  }));

  const optionsForSelect = optionsWithDisabled.map(option => {
    if (option.isDisabled) {
      return { ...option, label: `${option.label} (Disabled)`, value: option.value };
    }
    return option;
  });

  const getLogicalIndex = index => {
    const niceToHaveItems = checklist && checklist.filter(item => item.required)?.length;
    return niceToHaveItems ? index + niceToHaveItems : index;
  };

  useEffect(() => {
    dispatch({
      type: 'GET_CHECKLIST',
      jobOpeningId: interview?.interviewData?.Posting_Title.id,
    });
  }, [interview?.interviewData?.Posting_Title?.id]);

  useEffect(() => {
    if (state?.checklistItems?.checks) {
      const checklistScreenerItems = JSON.parse(state?.checklistItems.checks).filter(
        item => !item.internal && item.text !== '',
      );
      setChecklist(checklistScreenerItems);
    } else setChecklist([]);
  }, [state?.checklistItems]);

  return (
    <StyledAccordionItem title='Interview Report'>
      <Flex gap='12' mt='6px' alignItems='center'>
        <Flex flexDirection='column' flex='2'>
          <Flex alignItems='center'>
            <Text fontWeight='700'>Interview not done reason:</Text>
            <TooltipIcon helpText='Indicate the reason the interview was not done or leave blank otherwise.' />
            <Box minWidth='258px' marginLeft='24px'>
              <Controller
                name='Interview_Blocked_Reason'
                control={methods.control}
                render={({ field: { onChange, value } }) => {
                  const handleSelectChange = selectedOption => {
                    const selected = optionsWithDisabled.find(option => option.value === selectedOption.value);
                    if (selected && !selected.isDisabled) {
                      onChange(selectedOption.value);
                    }
                  };
                  return (
                    <Select
                      name='interviewNotDoneReason'
                      options={optionsForSelect}
                      selectedOptionStyle='check'
                      closeMenuOnSelect
                      chakraStyles={selectStyles}
                      onChange={handleSelectChange}
                      value={{ value: value || '-None-', label: value || '-None-' }}
                    />
                  );
                }}
              />
            </Box>
          </Flex>
        </Flex>
      </Flex>

      <Flex gap='12' mt='12px' alignItems='center'>
        <Flex flexDirection='column' flex='2'>
          <Flex alignItems='center'>
            <Text fontWeight='700'>Shadowing session:</Text>
            <TooltipIcon helpText='Indicate if you coached another screener in this session.' />
            <Controller
              name='Shadow_Session'
              control={methods.control}
              render={({ field: { onChange, value } }) => {
                const handleChange = selectedOption => {
                  onChange(selectedOption === 'true');
                  if (selectedOption === 'true') setShadowSession(true);
                  else setShadowSession(false);
                };
                return (
                  <RadioGroup size='lg' onChange={handleChange} value={value ? 'true' : 'false'} ml='24px'>
                    <Stack spacing={4} direction='row'>
                      <Radio value='true' mr='16px'>
                        <Text fontSize='14px'>Yes</Text>
                      </Radio>
                      <Radio value='false'>
                        <Text fontSize='14px'>No</Text>
                      </Radio>
                    </Stack>
                  </RadioGroup>
                );
              }}
            />
          </Flex>
        </Flex>
        <Flex flexDirection='column' flex='3'>
          <Flex alignItems='center'>
            <Text fontWeight='700' mr='24px' whiteSpace='nowrap'>
              Shadow Name:
            </Text>
            <Controller
              name='Trainees_Name'
              control={methods.control}
              render={({ field: { onChange, value } }) => (
                <InputBox value={value} onChange={onChange} type='text' disabled={!shadowSession} />
              )}
            />
          </Flex>
        </Flex>
      </Flex>
      <Flex alignItems='center' mt='24px'>
        <Text fontWeight='700' mr='24px' whiteSpace='nowrap'>
          Video Recording URL:
        </Text>
        <Controller
          name='Interview_URL'
          control={methods.control}
          render={({ field: { onChange, value } }) => <InputBox value={value} onChange={onChange} type='url' />}
        />
      </Flex>
      <Divider my='24px' />
      {checklist.length > 0 && checklist[0].text !== 'Test' && (
        <>
          <Flex flexDirection='column' gap={4}>
            <GreenTitleBox
              title='1. Job Opening Requirements:'
              helpText='Use this list of requirements to guide your interview preparation and execution. Review each item before the interview and mark “Meets Requirement” or “Doesn’t Meet Requirement” while you evaluate the candidate.'
              fontSize='14px'
            />
            {checklist.filter(item => item.required)[0] && (
              <>
                <Text fontWeight='400' fontStyle='italic' fontSize='12px' color='#838383'>
                  Must-have requirements
                </Text>
                <Flex flexDirection='column' gap={6}>
                  {checklist
                    .filter(item => item.required)
                    ?.map((item, index) => (
                      <Flex flexDirection='column' gap={4}>
                        <Controller
                          name={`Checklist_Items[${index}].Screeners_Decision`}
                          control={methods.control}
                          render={({ field: { onChange, value } }) => (
                            <>
                              <Flex>
                                <Text
                                  fontWeight='600'
                                  fontSize='12px'
                                  color={
                                    value === 'Meets requirement' || value === `Doesn't Meet requirement`
                                      ? 'blue.500'
                                      : '#454446'
                                  }>
                                  {item.text}
                                </Text>
                              </Flex>
                              <Flex flexDirection='row' fontSize='12px'>
                                <Text marginRight='16px'>Review:</Text>
                                <RadioGroup
                                  size='lg'
                                  onChange={selectedValue => {
                                    onChange(selectedValue); // Update form state

                                    // Prepare Amplitude event data
                                    const amplitudeEventProperties = {
                                      ChecklistRequirement:
                                        selectedValue === 'Meets requirement'
                                          ? true
                                          : selectedValue === `Doesn't Meet requirement`
                                          ? false
                                          : 'Not selected',
                                      ChecklistInputText: item.text,
                                      Type: item.required ? 'Must-have requirement' : 'Nice-to-have requirement',
                                      CandidateID: interview?.interviewData?.Candidate?.id || 'Unknown',
                                      CandidateName: interview?.interviewData?.Candidate?.name || 'Unknown',
                                      JobOpeningID: interview?.interviewData?.Posting_Title?.id || 'Unknown',
                                      JobOpeningName: interview?.interviewData?.Posting_Title?.name || 'Unknown',
                                    };

                                    // Send Amplitude event
                                    sendAmplitudeData('CHECKLIST_REVIEW_REQUIREMENTS', amplitudeEventProperties);
                                  }}
                                  value={value}>
                                  <Stack gap={4} direction='row'>
                                    <Radio value='Meets requirement'>
                                      <Text
                                        fontSize='12px'
                                        color={value === 'Meets requirement' ? 'blue.500' : 'black'}
                                        fontWeight={value === 'Meets requirement' ? '600' : '400'}>
                                        Meets Requirement
                                      </Text>
                                    </Radio>
                                    <Radio value={`Doesn't Meet requirement`}>
                                      <Text
                                        fontSize='12px'
                                        color={value === `Doesn't Meet requirement` ? 'blue.500' : 'black'}
                                        fontWeight={value === `Doesn't Meet requirement` ? '600' : '400'}>
                                        Doesn't Meet Requirement
                                      </Text>
                                    </Radio>
                                  </Stack>
                                </RadioGroup>
                              </Flex>
                              {(value === 'Meets requirement' || value === `Doesn't Meet requirement`) && (
                                <Flex alignItems='center' fontSize='12px'>
                                  <Text fontWeight='400' mr='24px' whiteSpace='nowrap'>
                                    Comment:
                                  </Text>
                                  <Controller
                                    name={`Checklist_Items[${index}].Screeners_Comment`}
                                    control={methods.control}
                                    render={({ field: { onChange: onCommentChange, value: commentValue } }) => (
                                      <InputBox
                                        value={commentValue}
                                        onChange={e => {
                                          onCommentChange(e); // Update the form value

                                          const amplitudeEventProperties = {
                                            ChecklistScreenerNote: e.target.value,
                                            ChecklistInputText: item.text,
                                            CandidateID: interview?.interviewData?.Candidate?.id || 'Unknown',
                                            CandidateName: interview?.interviewData?.Candidate?.name || 'Unknown',
                                            JobOpeningID: interview?.interviewData?.Posting_Title?.id || 'Unknown',
                                            JobOpeningName: interview?.interviewData?.Posting_Title?.name || 'Unknown',
                                          };

                                          const debouncedSendAmplitudeData = debounce(() => {
                                            sendAmplitudeData(
                                              'CHECKLIST_SCREENER_FEEDBACK_TEXT',
                                              amplitudeEventProperties,
                                            );
                                          }, 3000);
                                          debouncedSendAmplitudeData();
                                        }}
                                        type='text'
                                      />
                                    )}
                                  />
                                </Flex>
                              )}
                            </>
                          )}
                        />

                        <Controller
                          name={`Checklist_Items[${index}].Checklist_Item`}
                          control={methods.control}
                          defaultValue={item.text}
                          render={({ field: { onChange } }) => (
                            <InputBox value={item.text} onChange={onChange} type='hidden' />
                          )}
                        />
                      </Flex>
                    ))}
                </Flex>
              </>
            )}
            {checklist.filter(item => !item.required)[0] && (
              <>
                <Text fontWeight='400' fontStyle='italic' fontSize='12px' color='#838383' marginTop='12px'>
                  Nice-to-have requirements
                </Text>
                <Flex flexDirection='column' gap={6}>
                  {checklist
                    .filter(item => !item.required)
                    ?.map((item, index) => (
                      <Flex flexDirection='column' gap={4}>
                        <Controller
                          name={`Checklist_Items[${getLogicalIndex(index)}].Screeners_Decision`}
                          control={methods.control}
                          render={({ field: { onChange, value } }) => (
                            <>
                              <Flex>
                                <Text
                                  fontWeight='600'
                                  color={
                                    value === 'Meets requirement' || value === `Doesn't Meet requirement`
                                      ? 'blue.500'
                                      : '#454446'
                                  }
                                  fontSize='12px'>
                                  {item.text}
                                </Text>
                              </Flex>
                              <Flex flexDirection='row' fontSize='12px'>
                                <Text marginRight='16px'>Review:</Text>
                                <RadioGroup
                                  size='lg'
                                  onChange={selectedValue => {
                                    onChange(selectedValue); // Update form state

                                    // Prepare Amplitude event data
                                    const amplitudeEventProperties = {
                                      ChecklistRequirement:
                                        selectedValue === 'Meets requirement'
                                          ? true
                                          : selectedValue === `Doesn't Meet requirement`
                                          ? false
                                          : 'Not selected',
                                      ChecklistInputText: item.text,
                                      Type: item.required ? 'Must-have requirement' : 'Nice-to-have requirement',
                                      CandidateID: interview?.interviewData?.Candidate?.id || 'Unknown',
                                      CandidateName: interview?.interviewData?.Candidate?.name || 'Unknown',
                                      JobOpeningID: interview?.interviewData?.Posting_Title?.id || 'Unknown',
                                      JobOpeningName: interview?.interviewData?.Posting_Title?.name || 'Unknown',
                                    };

                                    // Send Amplitude event
                                    sendAmplitudeData('CHECKLIST_REVIEW_REQUIREMENTS', amplitudeEventProperties);
                                  }}
                                  value={value}>
                                  <Stack gap={4} direction='row'>
                                    <Radio value='Meets requirement'>
                                      <Text
                                        fontSize='12px'
                                        color={value === 'Meets requirement' ? 'blue.500' : 'black'}
                                        fontWeight={value === 'Meets requirement' ? '600' : '400'}>
                                        Meets Requirement
                                      </Text>
                                    </Radio>
                                    <Radio value={`Doesn't Meet requirement`}>
                                      <Text
                                        fontSize='12px'
                                        color={value === `Doesn't Meet requirement` ? 'blue.500' : 'black'}
                                        fontWeight={value === `Doesn't Meet requirement` ? '600' : '400'}>
                                        Doesn't Meet Requirement
                                      </Text>
                                    </Radio>
                                  </Stack>
                                </RadioGroup>
                              </Flex>
                              <Flex alignItems='center' fontSize='12px'>
                                {(value === 'Meets requirement' || value === `Doesn't Meet requirement`) && (
                                  <>
                                    <Text fontWeight='400' mr='24px' whiteSpace='nowrap'>
                                      Comment:
                                    </Text>
                                    <Controller
                                      name={`Checklist_Items[${getLogicalIndex(index)}].Screeners_Comment`}
                                      control={methods.control}
                                      render={({ field: { onChange: onCommentChange, value: commentValue } }) => (
                                        <InputBox
                                          value={commentValue}
                                          onChange={e => {
                                            onCommentChange(e); // Update the form value

                                            const amplitudeEventProperties = {
                                              ChecklistScreenerNote: e.target.value,
                                              ChecklistInputText: item.text,
                                              Type: item.required
                                                ? 'Must-have requirement'
                                                : 'Nice-to-have requirement',
                                              CandidateID: interview?.interviewData?.Candidate?.id || 'Unknown',
                                              CandidateName: interview?.interviewData?.Candidate?.name || 'Unknown',
                                              JobOpeningID: interview?.interviewData?.Posting_Title?.id || 'Unknown',
                                              JobOpeningName:
                                                interview?.interviewData?.Posting_Title?.name || 'Unknown',
                                            };

                                            const debouncedSendAmplitudeData = debounce(() => {
                                              sendAmplitudeData(
                                                'CHECKLIST_SCREENER_FEEDBACK_TEXT',
                                                amplitudeEventProperties,
                                              );
                                            }, 3000);
                                            debouncedSendAmplitudeData();
                                          }}
                                          type='text'
                                        />
                                      )}
                                    />
                                  </>
                                )}
                              </Flex>
                            </>
                          )}
                        />

                        {/* Hidden field to store Checklist_Item text */}
                        <Controller
                          name={`Checklist_Items[${getLogicalIndex(index)}].Checklist_Item`}
                          control={methods.control}
                          defaultValue={item.text}
                          render={({ field: { onChange } }) => (
                            <InputBox value={item.text} onChange={onChange} type='hidden' />
                          )}
                        />
                      </Flex>
                    ))}
                </Flex>
              </>
            )}
          </Flex>
          <Divider my='24px' />
        </>
      )}
      <Box>
        <GreenTitleBox
          title={`${checklist.length > 0 ? '2' : '1'}. Additional Notes:`}
          helpText='Include any additional information that was not covered in the job opening requirements but is relevant to the candidate’s profile. This could include industries they’ve worked in, types of companies they’ve been part of (e.g., startups), and any unique skills or achievements they mentioned during the conversation.'
        />
        <Controller
          name='Comments'
          control={methods.control}
          render={({ field: { onChange, value } }) => (
            <TextAreaBox text={interview.interviewData.Comments} height='144px' onChange={onChange} value={value} />
          )}
        />
      </Box>
      <Flex mt='10px' gap={3} direction='column'>
        <SimpleCheckbox name='Snapshot_US_experience' text='US Experience' default={false} />
        <SimpleCheckbox name='Snapshot_Start_ups_experience' text='Start-ups Experience' />
        <SimpleCheckbox name='Snapshot_Product_Oriented_Experience' text='Product Oriented Experience' />
      </Flex>
      <Divider my='24px' />
      <Flex gap='12'>
        <Box mb='-24px'>
          <GreenTitleBox
            title={`${checklist.length > 0 ? '3' : '2'}. Candidate's goals / next steps in his career:`}
            helpText="Write down everything related to the candidate's expectations, so we can fit the perfect match. Not only on salary, but perks, industries, roles, stacks, dreams, etc!"
          />
        </Box>
        <Flex flex='4'>
          <Box width='100%'>
            <Controller
              name='Candidate_goals'
              control={methods.control}
              render={({ field: { onChange, value } }) => (
                <TextAreaBox height='88px' onChange={onChange} value={value} />
              )}
            />
          </Box>
        </Flex>
      </Flex>
      <Divider my='24px' />
      <Flex gap='12'>
        <Flex flexDirection='column' flex='2'>
          <GreenTitleBox
            title={`${checklist.length > 0 ? '4' : '3'}. English Level:`}
            helpText="Select from the rank below the candidate's level of English. Take into account their vocabulary (both technical and general), accent, pronunciation and the ability to express complex ideas. Leave native for foreigners or people who have lived abroad and are fully bilingual."
          />
          <Controller
            name='English_level'
            control={methods.control}
            render={({ field: { onChange, value } }) => (
              <RadioGroup size='lg' onChange={onChange} value={value}>
                <Stack spacing={4}>
                  {metadata
                    .find(i => i.api_name === 'English_level')
                    .pick_list_values.filter(el => el.actual_value !== '-None-')
                    .map(j => {
                      return (
                        <Radio value={j.actual_value} key={j.actual_value}>
                          <Text fontSize='14px'>
                            {j.display_value === 'Not fluent enough(20% packet loss)'
                              ? 'Not fluent enough (+20% packet loss)'
                              : j.display_value}
                          </Text>
                        </Radio>
                      );
                    })}
                </Stack>
              </RadioGroup>
            )}
          />

          <Box mt='32px'>
            <Controller
              name='English_Comments'
              control={methods.control}
              render={({ field: { onChange, value } }) => (
                <TextAreaBox
                  title='Comments:'
                  helpText="Tell us what you have observed in the candidate's use of English supporting your choice above."
                  height='144px'
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </Box>
        </Flex>
        <Flex flexDirection='column' flex='2'>
          <GreenTitleBox
            title={`${checklist.length > 0 ? '5' : '4'}. Soft skills score:`}
            helpText='Choose a score from 1 to 5 and justify the selected one by choosing from the adjectives in Soft skills explanation.'
          />

          <Controller
            name='Soft_skills'
            control={methods.control}
            render={({ field: { onChange, value } }) => (
              <RadioGroup size='lg' onChange={onChange} value={value}>
                <Stack spacing={4}>
                  <Radio value='1' mr='24px'>
                    <Text fontSize='14px'>1 - Couldn't demonstrate good soft skills</Text>
                  </Radio>
                  <Radio value='2'>
                    <Text fontSize='14px'>
                      2 - Could demonstrate some good soft skills, needs to improve in at least 3 aspects
                    </Text>
                  </Radio>
                  <Radio value='3'>
                    <Text fontSize='14px'>3 - Showed adequate soft skills, can improve in at least two aspects</Text>
                  </Radio>
                  <Radio value='4'>
                    <Text fontSize='14px'>4 - Denoted good soft skills, can improve in one aspect</Text>
                  </Radio>
                  <Radio value='5'>
                    <Text fontSize='14px'>5 - Demonstrated strong soft skills</Text>
                  </Radio>
                </Stack>
              </RadioGroup>
            )}
          />

          <Box mt='32px'>
            <Flex alignItems='center' mb='12px'>
              <Text>Soft skills Explanation:</Text>
              <TooltipIcon helpText='Based on your score, choose the adjectives that best describe the candidate. Select one adjective per line or pair. If none of the adjectives apply, leave lines unselected.' />
            </Flex>
            <Flex flexDirection='column' gap='5' maxWidth='380px'>
              <SoftSkillsCheckbox skillA='Assertive' skillB='Unassertive' />
              <SoftSkillsCheckbox skillA='Extroverted' skillB='Introverted' />
              <SoftSkillsCheckbox skillA='Clear' skillB='Messy - Disordered' />
              <SoftSkillsCheckbox skillA='Concise' skillB='Talkative - wordy' />
              <SoftSkillsCheckbox skillA='Professional' skillB='Too Informal' />
              <SoftSkillsCheckbox skillA='Kind- Friendly' skillB='Cold - Distant' />
              <SoftSkillsCheckbox skillA='Good Listener' skillB='Poor Listener' />
            </Flex>
          </Box>
        </Flex>
      </Flex>
      <Divider my='24px' />
      <Flex gap='12' mb='8px'>
        <Flex flexDirection='column' flex='2'>
          <GreenTitleBox
            title={`${checklist.length > 0 ? '6' : '5'}. Monthly Salary (in USD):`}
            helpText='Indicate expected and monthly salary in USD. You can leave this field BLANK if salary is not provided.'
          />
          <Flex gap='12'>
            <Flex alignItems='center'>
              <Text mr='16px'>Expected:</Text>

              <Controller
                name='Expected_monthly_salary_USD'
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                  <InputBox
                    type='text'
                    width='75px'
                    value={value}
                    borderColor='inherits'
                    onChange={e => onChange(handlerSalaryInputOnChange(e))}
                  />
                )}
              />
            </Flex>
            <Flex alignItems='center'>
              <Text mr='16px'>Current:</Text>
              <Controller
                name='Current_monthly_salary_USD'
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                  <InputBox
                    type='text'
                    width='75px'
                    value={value}
                    borderColor='inherits'
                    onChange={e => onChange(handlerSalaryInputOnChange(e))}
                  />
                )}
              />
            </Flex>
          </Flex>
          <Flex alignItems='center' paddingTop='10px'>
            <Controller
              name='Comments_on_salary'
              control={methods.control}
              render={({ field: { onChange, value } }) => (
                <TextAreaBox
                  title='Comments on salary:'
                  helpText='Please add comments that add up info about candidate’s expected salary such as other currency (if not USD), current contract type, current benefits, exchange rates, etc.'
                  onChange={onChange}
                  value={value}
                  height='144px'
                  width='400px'
                />
              )}
            />
          </Flex>
        </Flex>
        <Flex flexDirection='column' flex='2'>
          <GreenTitleBox
            title={`${checklist.length > 0 ? '7' : '6'}. In another interview process?`}
            helpText='Indicate if the candidate is in one or more interview processes.'
          />

          <Controller
            name='In_another_interview_process'
            control={methods.control}
            render={({ field: { onChange, value } }) => {
              const handleChange = selectedOption => {
                onChange(selectedOption === 'true');
              };
              return (
                <RadioGroup size='lg' value={value ? 'true' : 'false'} onChange={handleChange}>
                  <Stack spacing={4} direction='row'>
                    <Radio value='true' mr='16px'>
                      <Text fontSize='14px'>Yes</Text>
                    </Radio>
                    <Radio value='false'>
                      <Text fontSize='14px'>No</Text>
                    </Radio>
                  </Stack>
                </RadioGroup>
              );
            }}
          />
        </Flex>
      </Flex>
      <Divider my='24px' />

      <StrongestTechStack interview={interview} experienceMetadata={experienceMetadata} checklist={checklist} />
      <Divider my='24px' />

      <GreenTitleBox
        title={`${checklist.length > 0 ? '9' : '8'}. Flags:`}
        helpText='Have you observed any undesired behavior/something you consider we should explore further? If YES, choose form the list below'
      />
      <Flex gap='12'>
        <Flex flexDirection='column' flex='2'>
          <Text fontWeight='700' mb='16px'>
            Yellow Flags:
          </Text>
          {/* <Text mb="12px">Communication Skills:</Text> */}
          <Flex flexDirection='column' ml='16px' gap='5' mb='32px'>
            <FlagsCheckboxes
              flagsArray={metadata.find(i => i.api_name === 'Yellow_flag_details').pick_list_values}
              flagsApiName='Yellow_flag_details'
            />
          </Flex>
          {/* <Text mb="12px">Thoughts & Attitudes:</Text> */}
        </Flex>
        <Flex flexDirection='column' flex='2'>
          <Text fontWeight='700' mb='16px'>
            Red Flags:
          </Text>
          {/* <Text mb="12px">Communication Skills:</Text> */}
          <Flex flexDirection='column' ml='16px' gap='5' mb='32px'>
            <FlagsCheckboxes
              flagsArray={metadata.find(i => i.api_name === 'Red_flag_details').pick_list_values}
              flagsApiName='Red_flag_details'
            />
          </Flex>
          {/* <Text mb="12px">Thoughts & Attitudes:</Text> */}
        </Flex>
      </Flex>
      <Flex gap='12' mb='8px'>
        <Box width='100%'>
          <Controller
            name='Flags_Comments'
            control={methods.control}
            render={({ field: { onChange, value } }) => (
              <TextAreaBox
                title='Comments:'
                helpText="Feel free to explain what you've seen."
                height='88px'
                onChange={onChange}
                value={value}
              />
            )}
          />
        </Box>
      </Flex>
    </StyledAccordionItem>
  );
}

export default InterviewAccordion;
